import { useWinSize } from '@contexts/WinSizeContext';
import classNames from 'classnames';
import React, { useState, useEffect } from 'react';
import './Accordion.scss';

interface ContainerProps {
  className?: string;
  children: React.ReactNode;
}

const Container = ({ className, children }: ContainerProps) => {
  return <div className={`c-accordion-tabs ${className}`}>{children}</div>;
};

interface Tab {
  id: string | number;
  name: string | React.ReactNode;
}

interface SectionProps {
  isOpen?: boolean;
  index?: number;
  // used to open first accordion of a specific block on mobile
  accordionBlockSelector?: string;
  onClick?: (tab: Tab) => void;
  children: React.ReactNode;
  tab: Tab;
  className?: string;
  url?: string | null;
  iconUrl?: string;
  hasIcon?: boolean;
  isLight?: boolean;
}

const Section = ({
  isOpen,
  index,
  onClick,
  accordionBlockSelector = '#RelatedTabs',
  children,
  tab,
  className,
  url,
  iconUrl,
  isLight,
  hasIcon,
}: SectionProps) => {
  const tabHead = '.c-accordion-tabs-head';
  const tabBody = '.c-accordion-tabs-body';
  const firstAccordionStyle = 'c-accordion-tabs-is-open';

  const [isOpened, setIsOpened] = useState<boolean>(!!isOpen);
  const { isDesktop } = useWinSize();
  const isMobile = !isDesktop;

  useEffect(() => {
    openFirstAccordion();

    if (isDesktop) {
      window.addEventListener('resize', openFirstAccordion);
      return () => {
        window.removeEventListener('resize', openFirstAccordion);
      };
    }
  }, []);

  const handleClick = (tab: Tab) => {
    const firstAccordionIsOpen =
      index == 0 &&
      document
        .querySelector(`${accordionBlockSelector} ${tabHead}`)
        .classList.contains(firstAccordionStyle);

    if (firstAccordionIsOpen) {
      closeFirstAccordion();
    } else {
      setIsOpened(!isOpened);
    }

    if (onClick) onClick(tab);
  };

  // To open 1st accordion(related tabs) by default for screens < 800 px
  const openFirstAccordion = () => {
    const accordionHead = document.querySelector(`${accordionBlockSelector} ${tabHead}`);
    const accordionBody = document.querySelector(`${accordionBlockSelector} ${tabBody}`);
    if (window.innerWidth < 800 && accordionHead && accordionBody) {
      accordionHead.classList.add(firstAccordionStyle);
      accordionBody.classList.add(firstAccordionStyle);
    }
  };

  // To close 1st accordion(related tabs) conditionally for screens < 800 px as state is common for all accordions
  const closeFirstAccordion = () => {
    const accordionHead = document.querySelector(`${accordionBlockSelector} ${tabHead}`);
    const accordionBody = document.querySelector(`${accordionBlockSelector} ${tabBody}`);
    if (window.innerWidth < 800 && accordionHead && accordionBody) {
      accordionHead.classList.remove(firstAccordionStyle);
      accordionBody.classList.remove(firstAccordionStyle);
      setIsOpened(false);
    }
  };

  const itemheading = !(className || isMobile) ? 'c-accordion-tabs-is-open' : '';

  return (
    <div className='c-accordion-tabs-wrapper'>
      <div
        onClick={() => handleClick(tab)}
        className={classNames(
          `c-accordion-tabs-head ${className}`,
          `c-tabs-${tab.id}`,
          itemheading || '',
          isOpened ? 'c-accordion-tabs-is-open' : '',
          isLight ? 'c-accordion-tabs-head-light' : ''
        )}
      >
        <div className='c-accordion-tabs-head-container is-bound'>
          {hasIcon && (
            <div
              className='c-accordion-tabs-head-title-icon'
              style={{ background: `url(${iconUrl}) no-repeat` }}
            />
          )}
          <h4 className='c-accordion-tabs-head-title'>
            {url !== null ? (
              <a href={url}>
                <span onClick={(event) => event.stopPropagation()}>{tab.name}</span>
              </a>
            ) : (
              tab.name
            )}
          </h4>
          {(!!className || isMobile) && (
            <i className='c-accordion-tabs-chevron-down c-accordion-tabs-head-icon' />
          )}
        </div>
      </div>
      <div
        className={`c-accordion-tabs-body ${itemheading} ${
          isOpened ? 'c-accordion-tabs-is-open' : ''
        }`}
      >
        {children}
      </div>
    </div>
  );
};

export { Container, Section };
